import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Pencil, UserCircle, AlertTriangle } from 'lucide-react';
import '../../App.css';

const fieldLabels = {
  cdlExpiration: 'CDL expiration',
  medicalCardExpiration: 'Medical card expiration',
};

function daysUntilDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.ceil((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

function expirationStyle(days) {
  if (days == null) return { color: '#6B7280', className: '' };
  if (days < 0) return { color: 'var(--danger)', className: 'text-expired' };
  if (days <= 30) return { color: 'var(--warning)', className: 'text-expiring' };
  return { color: 'var(--success)', className: '' };
}

function formatDisplayDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const DriverCard = ({ driver, onEdit, onDelete }) => {
  const navigate = useNavigate();
  const compliant = driver.status === 'compliant';
  const cdlDays = daysUntilDate(driver.cdlExpiration);
  const medDays = daysUntilDate(driver.medicalCardExpiration);
  const cdlStyle = expirationStyle(cdlDays);
  const medStyle = expirationStyle(medDays);

  const next = driver.nextExpiringItem;
  let nextAlert = null;
  if (next?.date) {
    const nd = daysUntilDate(next.date);
    if (nd != null && nd <= 30) {
      const label = fieldLabels[next.field] || next.field;
      nextAlert = {
        urgent: nd < 0,
        text:
          nd < 0
            ? `${label} expired on ${formatDisplayDate(next.date)}.`
            : `${label} expires in ${nd} day${nd === 1 ? '' : 's'} (${formatDisplayDate(next.date)}).`,
      };
    }
  }

  return (
    <div
      className="vehicle-card driver-card"
      tabIndex={0}
      onClick={() => navigate(`/fleet/drivers/${driver.id}`)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate(`/fleet/drivers/${driver.id}`);
        }
      }}
      style={{ cursor: 'pointer' }}
      aria-label={`View profile for ${driver.name}`}
    >
      <div className="vehicle-card-header">
        <div className="vehicle-icon-large">
          <UserCircle size={40} />
        </div>
        <div className="vehicle-actions">
          <button
            type="button"
            className="action-icon-button"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(driver);
            }}
            title="Edit"
          >
            <Pencil size={18} />
          </button>
          <button
            type="button"
            className="action-icon-button delete"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(driver);
            }}
            title="Delete"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>

      <div className="vehicle-card-body">
        <h3 className="vehicle-name">
          <Link
            to={`/fleet/drivers/${driver.id}`}
            className="fleet-driver-name-link"
            onClick={(e) => e.stopPropagation()}
          >
            {driver.name}
          </Link>
        </h3>

        <div className="status-badges" style={{ marginBottom: '12px' }}>
          <div
            className={`status-badge ${compliant ? 'status-defects-none' : 'status-defects-open'}`}
            style={{
              backgroundColor: compliant ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
              color: compliant ? 'var(--success)' : 'var(--danger)',
              border: `1px solid ${compliant ? 'var(--success)' : 'var(--danger)'}`,
            }}
          >
            {compliant ? 'Compliant' : 'Non-Compliant'}
          </div>
        </div>

        <div className="vehicle-details">
          {(driver.cdlNumber || driver.cdlState) && (
            <div className="vehicle-detail-row">
              <span className="detail-label">CDL:</span>
              <span className="detail-value">
                {[driver.cdlNumber, driver.cdlState].filter(Boolean).join(' · ') || '—'}
              </span>
            </div>
          )}
          <div className="vehicle-detail-row">
            <span className="detail-label">CDL expires:</span>
            <span className={`detail-value ${cdlStyle.className}`} style={{ color: cdlStyle.color }}>
              {formatDisplayDate(driver.cdlExpiration)}
            </span>
          </div>
          <div className="vehicle-detail-row">
            <span className="detail-label">Medical expires:</span>
            <span className={`detail-value ${medStyle.className}`} style={{ color: medStyle.color }}>
              {formatDisplayDate(driver.medicalCardExpiration)}
            </span>
          </div>
        </div>

        <div className="status-badges">
          <div
            className={`status-badge ${driver.clearinghouseRegistered ? 'status-inspection-valid' : 'status-inspection-overdue'}`}
          >
            Clearinghouse: {driver.clearinghouseRegistered ? 'Yes' : 'No'}
          </div>
          <div
            className={`status-badge ${driver.drugTestCurrent ? 'status-inspection-valid' : 'status-inspection-overdue'}`}
          >
            Drug test current: {driver.drugTestCurrent ? 'Yes' : 'No'}
          </div>
        </div>

        {nextAlert && (
          <div
            className="driver-next-expiring-alert"
            style={{
              marginTop: '12px',
              padding: '10px 12px',
              borderRadius: '8px',
              fontSize: '0.875rem',
              display: 'flex',
              gap: '8px',
              alignItems: 'flex-start',
              backgroundColor: nextAlert.urgent ? 'rgba(239, 68, 68, 0.12)' : 'rgba(245, 158, 11, 0.12)',
              border: `1px solid ${nextAlert.urgent ? 'var(--danger)' : 'var(--warning)'}`,
              color: nextAlert.urgent ? 'var(--danger)' : 'var(--warning)',
            }}
          >
            <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: '2px' }} />
            <span>{nextAlert.text}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default DriverCard;
